import React, { useState, useEffect } from 'react';
import { Copy, Check, Share2, Link as ChainIcon } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import { ThinkingBlock } from './ThinkingBlock';
import { CodeBlock } from './CodeBlock';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message, MessageContent as MessageContentType } from '../types/models';

interface MessageContentProps {
  message: Message;
  copiedMessageId: string | null;
  handleCopy: (id: string, content: string) => void;
  handleShare: (content: string) => void;
}

// Helper function to get text content from MessageContent type
const getTextContent = (content: MessageContentType): string => {
  if (typeof content === 'string') {
    return content;
  }
  return content.find(c => c.type === 'text')?.text || '';
};

const MarkdownRenderer: React.FC<{
  content: string;
  isAssistant: boolean;
  messageId: string;
}> = ({ content, isAssistant, messageId }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    className={`prose prose-sm w-full max-w-full dark:prose-invert break-words ${!isAssistant ? 'text-white' : 'dark:text-gray-100'}`}
    components={{
      img: ({src, alt}) => (
        <img src={src} alt={alt} className="max-w-full h-auto rounded-md" />
      ),
      pre: ({children}) => (
        <pre className="whitespace-pre-wrap break-words w-full">{children}</pre>
      ),
      li: ({...props}) => {
        const key = `${messageId}-li-${Math.random().toString(36).substr(2, 9)}`;
        return <li key={key} {...props} />;
      },
      a: ({href, children}) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
        >
          {children}
          <ChainIcon className="w-4 h-4" />
        </a>
      ),
      code: ({className, children, ...props}) => {
        const match = /language-(\w+)/.exec(className || '');
        return match ? (
          <CodeBlock className={className}>
            {String(children).replace(/\n$/, '')}
          </CodeBlock>
        ) : (
          <code className="bg-gray-800 text-gray-200 rounded px-1" {...props}>
            {children}
          </code>
        );
      }
    }}
  >
    {content}
  </ReactMarkdown>
);

export const MessageContent: React.FC<MessageContentProps> = ({
  message,
  copiedMessageId,
  handleCopy,
  handleShare
}) => {
  const isAssistant = message.role === 'assistant';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check initially
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const renderContent = () => {
    if (typeof message.content === 'string') {
      if (message.content.includes('<think>')) {
        return <ThinkingBlock>{message.content}</ThinkingBlock>;
      }
      return <MarkdownRenderer content={message.content} isAssistant={isAssistant} messageId={message.id} />;
    }

    return (
      <div className="flex flex-col gap-4">
        {message.content.map((content, index) => {
          if (content.type === 'image_url' && content.image_url) {
            return (
              <img
                key={`${message.id}-img-${index}`}
                src={content.image_url.url}
                alt="User uploaded content"
                className="max-w-full h-auto rounded-md"
              />
            );
          }
          if (content.type === 'text' && content.text) {
            return (
              <MarkdownRenderer
                key={`${message.id}-text-${index}`}
                content={content.text}
                isAssistant={isAssistant}
                messageId={message.id}
              />
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex w-full">
        <div
          className={`p-3 pb-4 rounded-lg relative ${
            !isAssistant
              ? 'bg-gray-600 dark:bg-gray-700 text-white dark:text-gray-100 ml-auto'
              : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-gray-700'
          }`}
          style={{
            width: isMobile && isAssistant ? '85%' : '70%',
            maxWidth: isMobile && isAssistant ? 'min(85%, 800px)' : 'min(70%, 800px)',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden'
          }}
        >
          {renderContent()}
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => {
                if (message.id) {
                  handleCopy(message.id, getTextContent(message.content));
                }
              }}
              className="p-1.5 rounded-lg hover:bg-white/10 dark:hover:bg-gray-600/50 transition-colors"
            >
              {copiedMessageId === message.id ? (
                <Check className="w-4 h-4 text-green-500 dark:text-dark-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500 dark:text-dark-gray-500" />
              )}
            </button>
            {typeof navigator.share !== 'undefined' && (
              <button
                onClick={() => handleShare(getTextContent(message.content))}
                className="p-1.5 rounded-lg hover:bg-white/10 dark:hover:bg-gray-600/50 transition-colors"
                aria-label="Share message"
              >
                <Share2 className="w-4 h-4 text-gray-500 dark:text-dark-gray-500" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={`text-xs text-gray-500 dark:text-gray-400 mt-2 ${!isAssistant ? 'text-right' : 'text-left'}`}>
        {formatDate(message.timestamp)}
      </div>
    </div>
  );
};